// Cross-tab auth sync. When a tab completes a login (incl. signup and email-confirm, which may switch
// accounts), logs out, or enters the unconfirmed limbo (confirm-email screen — the shared cookie now
// belongs to the unconfirmed account), it broadcasts here so every OTHER tab of this browser follows:
// on 'login' they navigate to /a and reboot into the new session (cookie + localStorage are shared
// across tabs), on 'logout' they wipe their session and reboot onto /logged-out (or the anonymous
// start page if they never held a session — see the listener in App.tsx). Deliberately
// separate from the feed engine's channel ('downread-feed-engine'): that one lives only while an
// authenticated engine exists and is gated to the same account id, so it can't reach anon tabs or a
// different account.

const AUTH_SYNC_CHANNEL = 'downread-auth';

export interface AuthSyncMessage {
    type: 'login' | 'logout';
}

// ONE channel per tab, shared by send and listen. BroadcastChannel only withholds a message from the
// exact channel OBJECT it was posted on — a second instance in the same tab WOULD receive it — so
// posting and listening on the same instance is what keeps a tab from reacting to its own broadcasts
// (e.g. the signup tab must stay on /confirm-email while its limbo 'logout' logs the OTHER tabs out).
let channel: BroadcastChannel | undefined;
function getChannel(): BroadcastChannel | undefined {
    if (typeof BroadcastChannel == 'undefined') return undefined;
    return channel ??= new BroadcastChannel(AUTH_SYNC_CHANNEL);
}

// Broadcast an auth change to the other tabs (never delivered back to this one, see above).
export function broadcastAuthChange(type: AuthSyncMessage['type']): void {
    getChannel()?.postMessage({ type } satisfies AuthSyncMessage);
}

// Listen for auth changes made in other tabs. Returns an unsubscribe function.
export function listenAuthSync(handler: (msg: AuthSyncMessage) => void): () => void {
    const ch = getChannel();
    if (!ch) return () => {};
    const listener = (event: MessageEvent<AuthSyncMessage>) => {
        const type = event.data?.type;
        if (type == 'login' || type == 'logout') handler(event.data);
    };
    ch.addEventListener('message', listener);
    return () => ch.removeEventListener('message', listener);
}
